import type {ReactElement} from 'react'
import {Popover} from '@freckle/educator-materials/ts/components/popover'
import {OverlayTrigger} from '../overlay-trigger'
import {
  trisectionProgress,
  progressBarDefault,
  trisectionProgressBarStatPopover,
  trisectionStatPopoverContent,
} from './trisection-progress-bar.module.scss'

type ProgressBarProps = {
  statPercentage: number
  className: string
}

type StatusTextT = string | ReactElement

function ProgressBar(props: ProgressBarProps) {
  const {className, statPercentage} = props

  return (
    <div
      className={`${trisectionProgress} ${className}`}
      role="progressbar"
      aria-valuenow={statPercentage}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{width: `${statPercentage}%`}}
    />
  )
}

type ProgressBarWithOverlayProps = {
  statNum: number
  statPercentage: number
  statusText: StatusTextT
  className: string
}

function ProgressBarWithOverlay(props: ProgressBarWithOverlayProps) {
  const {statNum, statPercentage, statusText, className} = props

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement="top"
      overlay={getCompletionPopover(statNum, statusText)}
    >
      <ProgressBar className={className} statPercentage={statPercentage} />
    </OverlayTrigger>
  )
}

function getCompletionPopover(statNum: number, statusText: StatusTextT) {
  return (
    <Popover id="trisection-progress-bar-stat-popover" className={trisectionProgressBarStatPopover}>
      <div className={trisectionStatPopoverContent}>
        <h1>{statNum}</h1>
        <p>{statusText}</p>
      </div>
    </Popover>
  )
}

type Props = {
  leftStat: number
  middleStat: number
  rightStat: number
  leftPopoverText: StatusTextT
  middlePopoverText: StatusTextT
  rightPopoverText: StatusTextT
  leftClassName: string
  middleClassName: string
  rightClassName: string
}

export function TriSectionProgressBar(props: Props) {
  const {
    leftStat,
    middleStat,
    rightStat,
    leftPopoverText,
    middlePopoverText,
    rightPopoverText,
    leftClassName,
    middleClassName,
    rightClassName,
  } = props

  const total = leftStat + middleStat + rightStat
  const leftPercentage = total !== 0 ? Math.floor((leftStat / total) * 100) : 0
  const middlePercentage = total !== 0 ? Math.floor((middleStat / total) * 100) : 0
  const rightPercentage = total !== 0 ? Math.ceil((rightStat / total) * 100) : 0
  const defaultPercentage = 100 - leftPercentage - middlePercentage - rightPercentage

  return (
    <div className={trisectionProgress}>
      <ProgressBarWithOverlay
        statNum={leftStat}
        statPercentage={leftPercentage}
        statusText={leftPopoverText}
        className={leftClassName}
      />

      <ProgressBarWithOverlay
        statNum={middleStat}
        statPercentage={middlePercentage}
        statusText={middlePopoverText}
        className={middleClassName}
      />

      <ProgressBarWithOverlay
        statNum={rightStat}
        statPercentage={rightPercentage}
        statusText={rightPopoverText}
        className={rightClassName}
      />

      <ProgressBar className={progressBarDefault} statPercentage={defaultPercentage} />
    </div>
  )
}
